import React, { useEffect, useState } from "react";
import { useTheme } from "../config/ThemeContext";
import { createPortal } from "react-dom";
import {
  TEAMSIDE_HOME,
  TEAMSIDE_AWAY,
  BALL_TYPE_HIGH,
  BALL_TYPE_MEDIUM,
  BALL_TYPE_QUICK,
  BALL_TYPE_TENSE,
  BALL_TYPE_SUPER,
  BALL_TYPE_FAST,
  BALL_TYPE_OTHER,
  SKILL_CODE_SERVE,
  SKILL_CODE_RECEPTION,
  SKILL_CODE_ATTACK,
  SKILL_CODE_BLOCK,
  SKILL_CODE_DIG,
  SKILL_CODE_SET,
  SKILL_CODE_FREEBALL,
  EFFECT_CODE_ERROR,
  EFFECT_CODE_RETURNED,
  EFFECT_CODE_POOR,
  EFFECT_CODE_BLOCKED_RETURN,
  EFFECT_CODE_GOOD,
  EFFECT_CODE_EXCELLENT,
  SPECIAL_EVENT_TEAM_TIMEOUT,
  SPECIAL_EVENT_TECH_TIMEOUT,
  SPECIAL_EVENT_SUBSTITUTION,
  SPECIAL_EVENT_LIBERO_IN_OUT,
  SPECIAL_EVENT_INJURY,
  SPECIAL_EVENT_RED_CARD,
  SPECIAL_EVENT_YELLOW_CARD,
  SPECIAL_EVENT_YELLOW_RED,
  SPECIAL_EVENT_PROTEST,
  SPECIAL_EVENT_VIDEO_CHALLENGE,
  SPECIAL_EVENT_CHALLENGE_OVERTURNED,
  SPECIAL_EVENT_CHALLENGE_UNSUCCESSFUL
} from "../config/matchEventConfig";

const ShortcutCategory = ({ title, shortcuts, theme }) => {
  return (
    <div className={`p-3 rounded-lg ${theme.neutral[100]} border ${theme.neutral.border}`}>
      <h3 className={`text-base font-semibold mb-2 ${theme.primary.text}`}>{title}</h3>
      <div className="space-y-2">
        {shortcuts.map(({ key, label, value }) => (
          <div key={key} className="flex items-center space-x-2">
            <span className={`font-mono px-2 py-1 rounded-md text-l font-bold ${theme.primary.lightBg} ${theme.primary.border} border`}>
              {key}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{label}</p>
              <p className="text-xs font-mono text-gray-500">{value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CodeCategory = ({ title, items, columns = 3, theme }) => {
  return (
    <div className={`p-4 rounded-lg ${theme.neutral[100]} border ${theme.neutral.border}`}>
      <h3 className={`text-lg font-semibold mb-3 ${theme.primary.text}`}>{title}</h3>
      <div className={`grid grid-cols-2 md:grid-cols-${columns} gap-3`}>
        {items.map(({ code, label }) => (
          <div key={code} className="flex items-center space-x-2">
            <span className={`font-mono px-2 py-1 rounded text-sm ${theme.neutral[200]} ${theme.neutral.border} border`}>
              {code}
            </span>
            <span className="text-sm text-gray-700">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScoutingHelpModal = ({ isOpen, onClose, inputRef }) => {
  const theme = useTheme();
  const [showCodeReference, setShowCodeReference] = useState(false);
  const shortcutGroups = [
    {
      title: "🏐 Serve",
      shortcuts: [
        { key: "W", label: "Service ace", value: `${SKILL_CODE_SERVE}${EFFECT_CODE_EXCELLENT}` },
        { key: "S", label: "Service error", value: `${SKILL_CODE_SERVE}${EFFECT_CODE_ERROR}` },
        { key: "X", label: "Serve in, opp. can attack", value: `${SKILL_CODE_SERVE}${EFFECT_CODE_POOR}` },
      ],
    },
    {
      title: "⚡ Attack",
      shortcuts: [
        { key: "Q", label: "Attack kill", value: `${SKILL_CODE_ATTACK}${EFFECT_CODE_EXCELLENT}` },
        { key: "Z", label: "Attack poor, easily dug", value: `${SKILL_CODE_ATTACK}${EFFECT_CODE_POOR}` },
        { key: "G", label: "Attack error", value: `${SKILL_CODE_ATTACK}${EFFECT_CODE_ERROR}` },
      ],
    },
    {
      title: "🎯 Reception",
      shortcuts: [
        { key: "R", label: "More than one position attack", value: `${SKILL_CODE_RECEPTION}${EFFECT_CODE_EXCELLENT}` },
        { key: "F", label: "One position attack", value: `${SKILL_CODE_RECEPTION}${EFFECT_CODE_GOOD}` },
        { key: "V", label: "Reception error", value: `${SKILL_CODE_RECEPTION}${EFFECT_CODE_ERROR}` },
      ],
    },
    {
      title: "🧱 Block",
      shortcuts: [
        { key: "I", label: "Monster block", value: `${SKILL_CODE_BLOCK}${EFFECT_CODE_EXCELLENT}` },
        { key: "K", label: "Block error", value: `${SKILL_CODE_BLOCK}${EFFECT_CODE_ERROR}` },
        { key: ".", label: "Can attack/block attempt", value: `${SKILL_CODE_BLOCK}${EFFECT_CODE_POOR}` },
      ],
    },
    {
      title: "🎯 Set",
      shortcuts: [
        { key: "J", label: "Set w/ blocker", value: `${SKILL_CODE_SET}${EFFECT_CODE_GOOD}` },
        { key: "U", label: "Perfect set (no blocker)", value: `${SKILL_CODE_SET}${EFFECT_CODE_EXCELLENT}` },
        { key: "N", label: "Set error", value: `${SKILL_CODE_SET}${EFFECT_CODE_ERROR}` },
      ],
    },
    {
      title: "🛡️ Dig",
      shortcuts: [
        { key: "E", label: "Can create an attack, well defended", value: `${SKILL_CODE_DIG}${EFFECT_CODE_EXCELLENT}` },
        { key: "D", label: "No attack / not properly dug", value: `${SKILL_CODE_DIG}${EFFECT_CODE_POOR}` },
        { key: "C", label: "Dig error / end rally", value: `${SKILL_CODE_DIG}${EFFECT_CODE_ERROR}` },
      ],
    },
  ];

  const codeCategories = [
    {
      title: "🏐 Skill Codes",
      items: [
        { code: SKILL_CODE_SERVE, label: "Serve" },
        { code: SKILL_CODE_RECEPTION, label: "Reception" },
        { code: SKILL_CODE_ATTACK, label: "Attack" },
        { code: SKILL_CODE_BLOCK, label: "Block" },
        { code: SKILL_CODE_DIG, label: "Dig" },
        { code: SKILL_CODE_SET, label: "Set" },
        { code: SKILL_CODE_FREEBALL, label: "Freeball" },
      ],
      columns: 4
    },
    {
      title: "🎾 Ball Types",
      items: [
        { code: BALL_TYPE_HIGH, label: "High is default if left blank" },
        { code: BALL_TYPE_MEDIUM, label: "Medium" },
        { code: BALL_TYPE_QUICK, label: "Quick" },
        { code: BALL_TYPE_TENSE, label: "Tense" },
        { code: BALL_TYPE_SUPER, label: "Super" },
        { code: BALL_TYPE_FAST, label: "Fast" },
        { code: BALL_TYPE_OTHER, label: "Other" },
      ],
      columns: 4
    },
    {
      title: "⚡ Effect Codes",
      items: [
        { code: EFFECT_CODE_ERROR, label: "Error" },
        { code: EFFECT_CODE_RETURNED, label: "Returned" },
        { code: EFFECT_CODE_POOR, label: "Counterattack possible" },
        { code: EFFECT_CODE_BLOCKED_RETURN, label: "Blocked and returned" },
        { code: EFFECT_CODE_GOOD, label: "Ball playable" },
        { code: EFFECT_CODE_EXCELLENT, label: "Point won" },
      ],
      columns: 3
    },
    {
      title: "⏳ Special Event Codes",
      items: [
        { code: SPECIAL_EVENT_TEAM_TIMEOUT, label: "Team Timeout" },
        { code: SPECIAL_EVENT_TECH_TIMEOUT, label: "Technical Timeout" },
        { code: SPECIAL_EVENT_SUBSTITUTION, label: "Substitution" },
        { code: SPECIAL_EVENT_LIBERO_IN_OUT, label: "Libero in/out" },
        { code: SPECIAL_EVENT_INJURY, label: "Injury" },
        { code: SPECIAL_EVENT_RED_CARD, label: "Red Card" },
        { code: SPECIAL_EVENT_YELLOW_CARD, label: "Yellow Card" },
        { code: SPECIAL_EVENT_YELLOW_RED, label: "Yellow + Red (Disqualified)" },
        { code: SPECIAL_EVENT_PROTEST, label: "Protest" },
        { code: SPECIAL_EVENT_VIDEO_CHALLENGE, label: "Challenge Request" },
        { code: SPECIAL_EVENT_CHALLENGE_OVERTURNED, label: "Challenge Overturned" },
        { code: SPECIAL_EVENT_CHALLENGE_UNSUCCESSFUL, label: "Challenge Unsuccessful" },
      ],
      columns: 4
    },
    {
      title: "💡 Team Side Codes",
      items: [
        { code: `${TEAMSIDE_HOME} or blank`, label: "Home Team" },
        { code: TEAMSIDE_AWAY, label: "Away Team" },
      ],
      columns: 2
    }
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        if (inputRef?.current) inputRef.current.focus();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent scrolling of the background when modal is open
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restore scrolling when modal is closed
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, inputRef]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className={`
          relative
          max-w-6xl w-full
          bg-white/95 backdrop-blur-sm
          rounded-xl shadow-2xl
          p-6 
          overflow-y-auto max-h-[90vh]
          ${theme.primary.border}
          transform transition-all
        `}>
          {/* Header */}
          <div className="flex justify-between items-center mb-6 sticky top-0 bg-white/95 backdrop-blur-sm py-2 -mt-2 -mx-2 px-2">
            <h2 className={`text-2xl font-bold ${theme.primary.text}`}>📘 Scouting Code Help</h2>
            <button
              onClick={onClose}
              className={`
                p-2 rounded-lg
                hover:bg-gray-100
                ${theme.primary.text}
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-blue-500
              `}
              aria-label="Close Help"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Keyboard Shortcuts */}
            <div>
              <h3 className={`text-xl font-semibold mb-4 ${theme.primary.text}`}>⌨️ Keyboard Shortcuts</h3>
              <p className="text-sm text-gray-600 mb-4">
                Press these keys to quickly input codes. For example: <kbd className={`px-2 py-1 rounded ${theme.neutral[200]} font-mono`}>1W</kbd> becomes <strong>Home team player 1 Service ace</strong> (<code className="font-mono">*1S#</code>)
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {shortcutGroups.map((group) => (
                  <ShortcutCategory 
                    key={group.title} 
                    title={group.title} 
                    shortcuts={group.shortcuts} 
                    theme={theme} 
                  />
                ))}
              </div>
            </div>

            {/* Code Reference */}
            <div className="space-y-2"> 
              <div className="flex items-baseline gap-2"> 
                <h3 className={`text-xl font-semibold ${theme.primary.text}`}>📝 Code Reference</h3>
                <button
                  onClick={() => setShowCodeReference(!showCodeReference)}
                  className={`text-sm font-medium ${theme.primary.text} hover:underline focus:outline-none`}
                >
                  [{showCodeReference ? 'hide' : 'show'}]
                </button>
              </div>
              
              {showCodeReference && (
                <div>
                  <div className="mb-6">
                    <h4 className={`text-lg font-semibold mb-2 ${theme.primary.text}`}>🔍 How to enter a Scouting Code</h4>
                    <div className="flex flex-wrap gap-2 text-sm font-mono mb-4">
                      <span className="px-2 py-1 bg-gray-100 rounded border border-gray-300">* or / <span className="text-gray-500">Team Side</span></span>
                      <span className="px-2 py-1 bg-gray-100 rounded border border-gray-300">99 <span className="text-gray-500">Player #</span></span>
                      <span className="px-2 py-1 bg-gray-100 rounded border border-gray-300">S <span className="text-gray-500">Skill</span></span>
                      <span className="px-2 py-1 bg-gray-100 rounded border border-gray-300">H <span className="text-gray-500">Ball Type</span></span>
                      <span className="px-2 py-1 bg-gray-100 rounded border border-gray-300">= <span className="text-gray-500">Effect</span></span>
                    </div>
                    
                    For example, <code className="bg-gray-100 px-1 py-0.5 rounded">*99SH=</code> means the home team player #99 performed a <strong>high serve</strong> that resulted in an <strong>error</strong>.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 	
                    {codeCategories.map((category) => (
                      <CodeCategory 
                        key={category.title}
                        title={category.title} 
                        items={category.items}
                        columns={category.columns}
                        theme={theme} 
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render the modal using a portal
  return createPortal(modalContent, document.body);
};

export default ScoutingHelpModal;
